import { isFeatureFlagKey } from '~~/layers/base/constants/featureFlag'

/**
 * Ejects a user from a gated route when its flag is turned off **while they are already on it**
 * (`SPEC_feature_flags_TK-12413.md`, FE-3).
 *
 * `middleware/feature-enabled.ts` only runs at navigation time, so it cannot help someone already
 * sitting inside the feature when an admin flips the kill-switch. This watches the resolved flag
 * map instead and reacts to the value changing under them.
 *
 * Implemented once here rather than per-page: it reads the same `featureFlag` page meta the
 * middleware uses, so every gated route is covered automatically and a new gated page needs no
 * extra wiring.
 *
 * Ejects to `/` (ADR-006), the same destination `middleware/feature-enabled.ts` uses, so entering
 * a killed feature and being removed from one behave identically.
 *
 * **Known gap:** this navigates away immediately. The SPEC calls for warning on unsaved work
 * first, which needs per-flow knowledge of what "unsaved" means (draft note, in-progress design).
 * Until that exists, a user editing a note when the flag flips loses unsaved edits. Tracked as
 * follow-up — the alternative, leaving them inside a killed feature, is worse.
 */
export default defineNuxtPlugin(() => {
    const route = useRoute()
    const featureFlagsStore = useFeatureFlagsStore()

    watch(
        () => featureFlagsStore.flags,
        (flags) => {
            const key = route.meta.featureFlag
            if (!key || !isFeatureFlagKey(key)) {
                return
            }
            // Only act on a definite `false`. While flags are still loading the map holds
            // fail-closed defaults, and ejecting on those would throw users out mid-load.
            if (!featureFlagsStore.hasLoadedOnce) {
                return
            }
            if (flags[key] === false) {
                void navigateTo('/', { replace: true })
            }
        },
        { deep: true }
    )
})
